import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type RefObject,
} from "react";
import { YoutubePlayerChrome, YOUTUBE_IFRAME_PLAYER_VARS } from "@repo/ui";

declare global {
  interface Window {
    YT?: {
      Player: new (
        host: string | HTMLElement,
        options: Record<string, unknown>,
      ) => YTPlayer;
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

type YTPlayer = {
  destroy: () => void;
  playVideo: () => void;
  pauseVideo: () => void;
  seekTo: (seconds: number, allowSeekAhead: boolean) => void;
  getCurrentTime?: () => number;
  getDuration?: () => number;
};

let iframeApiPromise: Promise<void> | null = null;

function loadYoutubeIframeApi(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("no window"));
  }
  if (window.YT?.Player) {
    return Promise.resolve();
  }
  if (!iframeApiPromise) {
    iframeApiPromise = new Promise<void>((resolve) => {
      const previous = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        try {
          previous?.();
        } finally {
          resolve();
        }
      };

      const existing = document.querySelector(
        'script[src="https://www.youtube.com/iframe_api"]',
      );
      if (!existing) {
        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        tag.async = true;
        document.body.appendChild(tag);
      }
    });
  }
  return iframeApiPromise;
}

export type SelfLearnYoutubePlayerHandle = {
  play: () => void;
  pause: () => void;
  restartFromBeginning: () => void;
  seekToSeconds: (seconds: number) => void;
  getCurrentTime: () => number;
  getDuration: () => number;
};

export type SelfLearnYoutubePlayerProps = {
  youtubeVideoId: string;
  className?: string;
  startSeconds?: number;
  /** Fullscreen the chrome into this element (e.g. the page video frame). */
  fullscreenTargetRef?: RefObject<HTMLElement | null>;
  hideControls?: boolean;
  onReady?: () => void;
  onPlay?: () => void;
  onPause?: () => void;
  /** Fires when playback time jumps (e.g. user seeked via custom controls). */
  onSeek?: (currentTime: number, duration: number) => void;
  onBuffering?: (buffering: boolean) => void;
  onEnded: () => void;
  onError: () => void;
  onProgress?: (currentTime: number, duration: number) => void;
};

const YT_ENDED = 0;
const YT_PLAYING = 1;
const YT_PAUSED = 2;
const YT_BUFFERING = 3;

/** Seconds; ignore smaller jumps (playback, minor API jitter). */
const SEEK_JUMP_THRESHOLD_SEC = 2.5;
const PAUSED_SEEK_POLL_MS = 400;

export const SelfLearnYoutubePlayer = forwardRef<
  SelfLearnYoutubePlayerHandle,
  SelfLearnYoutubePlayerProps
>(function SelfLearnYoutubePlayer(props, ref) {
  const {
    youtubeVideoId,
    className,
    startSeconds = 0,
    fullscreenTargetRef,
    hideControls = false,
    onReady,
    onPlay,
    onPause,
    onSeek,
    onBuffering,
    onEnded,
    onError,
    onProgress,
  } = props;

  /** YT mutates this node; keep it empty in React so reconciliation never fights the iframe API. */
  const playerMountRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<YTPlayer | null>(null);

  const [isPlaying, setIsPlaying] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const onReadyRef = useRef(onReady);
  const onPlayRef = useRef(onPlay);
  const onPauseRef = useRef(onPause);
  const onSeekRef = useRef(onSeek);
  const onBufferingRef = useRef(onBuffering);
  const onEndedRef = useRef(onEnded);
  const onErrorRef = useRef(onError);
  const onProgressRef = useRef(onProgress);
  const lastProgressTimeRef = useRef<number | null>(null);
  const durationRef = useRef(0);

  onReadyRef.current = onReady;
  onPlayRef.current = onPlay;
  onPauseRef.current = onPause;
  onSeekRef.current = onSeek;
  onBufferingRef.current = onBuffering;
  onEndedRef.current = onEnded;
  onErrorRef.current = onError;
  onProgressRef.current = onProgress;

  useEffect(() => {
    setIsPlaying(true);
    setCurrentTime(0);
    setDuration(0);
    durationRef.current = 0;
    lastProgressTimeRef.current = null;
  }, [youtubeVideoId]);

  const readPlaybackTimes = (): { t: number; d: number } | null => {
    const p = playerRef.current;
    if (!p?.getCurrentTime || !p.getDuration) return null;
    try {
      const t = p.getCurrentTime();
      const d = p.getDuration();
      if (!Number.isFinite(t) || !Number.isFinite(d) || d <= 0) return null;
      return { t, d };
    } catch {
      return null;
    }
  };

  const applyProgress = useCallback((t: number, d: number) => {
    setCurrentTime(t);
    if (d > 0) {
      setDuration(d);
      durationRef.current = d;
    }
    onProgressRef.current?.(t, d);
  }, []);

  const maybeReportSeek = (t: number, d: number) => {
    const onSeekCb = onSeekRef.current;
    if (!onSeekCb) return;
    const prev = lastProgressTimeRef.current;
    lastProgressTimeRef.current = t;
    if (prev == null) return;
    if (Math.abs(t - prev) > SEEK_JUMP_THRESHOLD_SEC) {
      onSeekCb(t, d);
    }
  };

  const handleChromeSeek = useCallback((seconds: number) => {
    const s = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
    playerRef.current?.seekTo?.(s, true);
    setCurrentTime(s);
    lastProgressTimeRef.current = s;
    const d = durationRef.current || readPlaybackTimes()?.d || 0;
    onSeekRef.current?.(s, d);
  }, []);

  const handleChromeTogglePlay = useCallback(() => {
    if (isPlaying) {
      playerRef.current?.pauseVideo?.();
      setIsPlaying(false);
      onPauseRef.current?.();
    } else {
      playerRef.current?.playVideo?.();
      setIsPlaying(true);
      onPlayRef.current?.();
    }
  }, [isPlaying]);

  useImperativeHandle(ref, () => ({
    play: () => {
      playerRef.current?.playVideo?.();
      setIsPlaying(true);
    },
    pause: () => {
      playerRef.current?.pauseVideo?.();
      setIsPlaying(false);
    },
    restartFromBeginning: () => {
      const p = playerRef.current;
      if (!p) return;
      p.seekTo?.(0, true);
      setCurrentTime(0);
      lastProgressTimeRef.current = 0;
      p.playVideo?.();
      setIsPlaying(true);
    },
    seekToSeconds: (seconds: number) => {
      handleChromeSeek(seconds);
    },
    getCurrentTime: () => readPlaybackTimes()?.t ?? currentTime,
    getDuration: () => readPlaybackTimes()?.d ?? duration,
  }));

  useEffect(() => {
    /** Snapshot once per player instance; parent `watchedTill` updates must not remount the iframe. */
    const resumeSeconds = Number.isFinite(startSeconds)
      ? Math.max(0, startSeconds)
      : 0;

    const mountEl = playerMountRef.current;
    if (!mountEl) return;

    let cancelled = false;
    let tick: number | undefined;
    let created: YTPlayer | null = null;

    const clearTick = () => {
      if (tick != null) {
        window.clearInterval(tick);
        tick = undefined;
      }
    };

    const destroy = () => {
      clearTick();
      try {
        created?.destroy?.();
      } catch {
        /* ignore */
      }
      created = null;
      playerRef.current = null;
    };

    void loadYoutubeIframeApi()
      .then(() => {
        if (cancelled || !window.YT?.Player) return;

        const origin =
          typeof window !== "undefined" && window.location?.origin
            ? window.location.origin
            : undefined;

        created = new window.YT.Player(mountEl, {
          videoId: youtubeVideoId,
          width: "100%",
          height: "100%",
          playerVars: {
            ...YOUTUBE_IFRAME_PLAYER_VARS,
            ...(origin ? { origin } : {}),
          },
          events: {
            onReady: (e: { target: YTPlayer }) => {
              if (cancelled) return;
              lastProgressTimeRef.current = null;
              playerRef.current = e.target;
              if (resumeSeconds > 0) {
                e.target.seekTo(resumeSeconds, true);
                setCurrentTime(resumeSeconds);
              }
              e.target.playVideo();
              setIsPlaying(true);
              onReadyRef.current?.();
            },
            onStateChange: (e: { data: number }) => {
              if (cancelled) return;
              const st = e.data;
              if (st === YT_BUFFERING) {
                onBufferingRef.current?.(true);
              } else if (st === YT_PLAYING) {
                onBufferingRef.current?.(false);
                setIsPlaying(true);
                onPlayRef.current?.();
                clearTick();
                const wantProgress = Boolean(onProgressRef.current);
                const wantSeek = Boolean(onSeekRef.current);
                if (wantProgress || wantSeek) {
                  tick = window.setInterval(() => {
                    const times = readPlaybackTimes();
                    if (!times) return;
                    const { t, d } = times;
                    if (wantSeek) {
                      maybeReportSeek(t, d);
                    } else {
                      lastProgressTimeRef.current = t;
                    }
                    if (wantProgress) {
                      applyProgress(t, d);
                    } else {
                      setCurrentTime(t);
                      if (d > 0) {
                        setDuration(d);
                        durationRef.current = d;
                      }
                    }
                  }, 1000);
                }
              } else if (st === YT_PAUSED) {
                onBufferingRef.current?.(false);
                setIsPlaying(false);
                clearTick();
                onPauseRef.current?.();
                if (onSeekRef.current) {
                  lastProgressTimeRef.current = null;
                  tick = window.setInterval(() => {
                    const times = readPlaybackTimes();
                    if (!times) return;
                    const { t, d } = times;
                    maybeReportSeek(t, d);
                    setCurrentTime(t);
                  }, PAUSED_SEEK_POLL_MS);
                }
              } else {
                onBufferingRef.current?.(false);
                clearTick();
              }

              if (st === YT_ENDED) {
                onBufferingRef.current?.(false);
                setIsPlaying(false);
                clearTick();
                lastProgressTimeRef.current = null;
                onEndedRef.current();
              }
            },
            onError: () => {
              if (cancelled) return;
              onErrorRef.current();
            },
          },
        }) as unknown as YTPlayer;

        playerRef.current = created;
      })
      .catch(() => {
        if (!cancelled) onErrorRef.current();
      });

    return () => {
      cancelled = true;
      destroy();
    };
  }, [applyProgress, youtubeVideoId]);

  return (
    <div className={className} style={{ width: "100%", height: "100%" }}>
      <YoutubePlayerChrome
        isPlaying={isPlaying}
        currentTime={currentTime}
        duration={duration}
        onTogglePlay={handleChromeTogglePlay}
        onSeek={handleChromeSeek}
        fullscreenTargetRef={fullscreenTargetRef}
        hideControls={hideControls}
        style={{ width: "100%", height: "100%" }}
      >
        <div
          ref={playerMountRef}
          style={{
            width: "100%",
            height: "100%",
            minHeight: 0,
          }}
        />
      </YoutubePlayerChrome>
    </div>
  );
});
